import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header'; 
import Footer from './Footer'; 

/**
 * Helper component to reset scroll position to top on page navigation
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function Layout() {
  const { pathname } = useLocation();
  
  // 根据路径生成页面标题
  const getPageTitle = () => {
    if (pathname === '/') return '欧洲葡萄酒之旅 - 专业的葡萄酒知识平台';
    
    const paths = pathname.split('/').filter(Boolean);
    const lastPath = paths[paths.length - 1];
    
    const titleMapping = {
      'learn-discover': '葡萄酒基础知识_品酒技巧 - 欧洲葡萄酒之旅',
      'region-grape': '欧洲葡萄酒产区_葡萄品种大全 - 欧洲葡萄酒之旅',
      'foodpairing-lifestyle': '葡萄酒美食搭配_餐酒搭配技巧 - 欧洲葡萄酒之旅',
      'health-beauty': '葡萄酒健康_红酒美容 - 欧洲葡萄酒之旅',
      'contact': '联系我们_葡萄酒咨询 - 欧洲葡萄酒之旅'
    };
    
    return titleMapping[lastPath] || '欧洲葡萄酒之旅 - 探索欧洲葡萄酒文化';
  };

  // 根据路径生成页面描述
  const getPageDescription = () => {
    if (pathname === '/') {
      return '欧洲葡萄酒之旅是中国专业的葡萄酒知识平台，提供欧洲葡萄酒产区介绍、葡萄品种详解、美食搭配技巧、品酒知识和葡萄酒文化。';
    }
    
    const paths = pathname.split('/').filter(Boolean);
    const lastPath = paths[paths.length - 1];
    
    const descriptionMapping = {
      'learn-discover': '学习葡萄酒基础知识，了解品酒技巧、葡萄酒分类、酿造工艺和储存方法。欧洲葡萄酒之旅为您提供专业的葡萄酒知识。',
      'region-grape': '探索法国波尔多、勃艮第，意大利托斯卡纳，西班牙里奥哈等欧洲著名葡萄酒产区。了解赤霞珠、黑皮诺、霞多丽等葡萄品种特点。',
      'foodpairing-lifestyle': '学习葡萄酒与中餐、西餐的完美搭配技巧。红酒配红肉、白酒配白肉的基本原则，以及更多餐酒搭配专业知识。',
      'health-beauty': '了解适量饮用葡萄酒对健康的益处，包括心血管健康、抗氧化等。探索葡萄酒在美容养生中的应用。',
      'contact': '联系我们获取更多葡萄酒资讯，咨询葡萄酒相关问题。欧洲葡萄酒之旅团队竭诚为您服务。'
    };
    
    return descriptionMapping[lastPath] || '欧洲葡萄酒之旅为您提供专业的欧洲葡萄酒资讯、产区指南和美食搭配建议。';
  };

  // 生成面包屑结构化数据（只对搜索引擎可见）
  const getBreadcrumbSchema = () => {
    if (pathname === '/' || typeof window === 'undefined') return null;
    
    const paths = pathname.split('/').filter(Boolean);
    const breadcrumbs = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": window.location.origin
      }
    ];
    
    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      
      const nameMapping = {
        'category': '分类',
        'learn-discover': '学习与探索',
        'region-grape': '产区与葡萄',
        'foodpairing-lifestyle': '美食搭配与生活方式',
        'health-beauty': '健康与美容',
        'contact': '联系我们',
        'post': '文章'
      };
      
      // 判断是否是文章页面
      const isPostPage = paths.includes('post');
      
      let name;
      if (isPostPage && index === paths.length - 1) {
        // 文章页面的最后一段：美化英文显示
        name = path.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      } else {
        name = nameMapping[path] || path.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
      
      breadcrumbs.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": name,
        "item": window.location.origin + currentPath
      });
    });
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs
    };
  };

  return (
    <>
      {/* SEO Meta Data - 完全不可见，不影响布局 */}
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getPageDescription()} />
        <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}${pathname}` : ''} />
        
        {/* 面包屑结构化数据 - 只对搜索引擎，不影响视觉 */}
        {pathname !== '/' && getBreadcrumbSchema() && (
          <script type="application/ld+json">
            {JSON.stringify(getBreadcrumbSchema())}
          </script>
        )}
        
        {/* 当前页面标记 - 只对搜索引擎 */}
        <meta name="page-type" content={pathname === '/' ? 'homepage' : 'category'} />
        <meta name="page-path" content={pathname} />
      </Helmet>

      {/* 布局结构 - 负边距减少header和carousel间距 */}
      <div className="min-h-screen flex flex-col bg-cream" id="site-wrapper">
        <ScrollToTop />

        {/* 1. HEADER LANDMARK */}
        <Header />

        {/* 2. SKIP LINK - 中文版本，但不影响布局 */}
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-[#656D4A] text-white p-4 z-50 rounded-lg shadow-lg"
        >
          跳转到主要内容
        </a>

        {/* 3. MAIN LANDMARK - 添加负边距减少间距 */}
        <main id="main-content" className="flex-grow -mt-6 md:-mt-8" role="main">
          <h1 className="sr-only">{getPageTitle()}</h1>
          <Outlet />
        </main>

        {/* 4. FOOTER LANDMARK */}
        <Footer />
      </div>
    </>
  );
}